// ─── Rythmes de garde hors vacances : TIMELINE de périodes ───────
// Le rythme n'est plus unique : c'est une LISTE de périodes chronologiques
//   { modeleId, debut, fin|null }   (la dernière a fin = null → « en cours »).
// Règle d'insertion (voulue) : une nouvelle période démarre APRÈS la dernière ;
// si sa date tombe dans la période courante, celle-ci est fermée la veille
// (fin = départ − 1 j). Modèles PERSONNALISÉS stockés à côté (grille N×7).
// Tout est PARTAGÉ MoPCal (docs calendriers/{calId}).
import { db } from '@mop/core';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getModele, MODELES_GARDE } from './modelesGarde';

const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
export const decalerDate = (dk, n) => { const d = parse(dk); d.setDate(d.getDate() + n); return fmt(d); };
export const frCourt = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const trier = (l) => [...(l || [])].sort((a, b) => (a.debut < b.debut ? -1 : a.debut > b.debut ? 1 : 0));

// Libellé d'un modèle (intégré ou personnalisé). `t` (useT) optionnel : sans lui,
// repli sur le label fr embarqué. Les modèles perso portent un nom utilisateur.
export const labelModeleGarde = (id, perso = [], t) => {
  const m = MODELES_GARDE.find(m => m.id === id);
  if (m) return (t && m.i18n) ? t(m.i18n) : m.label;
  const p = (perso || []).find(m => m.id === id);
  if (p) return p.nom || (t ? t('garde.mdlPerso') : 'Personnalisé');
  return t ? t('garde.mdlPerso') : 'Personnalisé';
};
// Description d'un modèle (intégré) ; perso → sa propre description libre.
export const descModeleGarde = (id, perso = [], t) => {
  const m = MODELES_GARDE.find(m => m.id === id);
  if (m) return (t && m.i18nDesc) ? t(m.i18nDesc) : m.desc;
  return (perso || []).find(m => m.id === id)?.desc || '';
};

// Construit la config de calcul (ancrée sur `debut`) pour un modèle intégré OU perso.
export function construireConfig(modeleId, debut, perso = []) {
  const integre = getModele(modeleId);
  if (integre) return integre.build(debut);
  const p = (perso || []).find(m => m.id === modeleId);
  if (p) {
    // Modèle calé sur les semaines du calendrier (ISO) : la date de début ne sert
    // qu'à démarrer l'application (bornage période), pas à ancrer le cycle.
    if (p.cale) return { mode: 'semaineCalee', nbSemaines: p.nbSemaines || 2, patternN: p.patternN, semaine1: p.semaine1 || 'paire' };
    return { mode: 'joursSpecifiques', ancre: debut, nbSemaines: p.nbSemaines || 2, patternN: p.patternN };
  }
  return null;
}

// ── Opérations sur la timeline (pures) ──────────────────────────
// Aperçu de l'insertion à une date donnée (sans muter) : quelle serait la date
// de FIN de la nouvelle période, et un éventuel motif de refus (recouvrement).
export function apercuInsertion(rythmes, debut) {
  const l = trier(rythmes);
  if (!debut) return { fin: null, err: null };
  if (l.some(p => p.debut === debut)) return { fin: null, err: 'garde.errDejaDate' };
  // Période qui CONTIENT la date de début → la nouvelle reprend sa fin.
  const contenante = l.find(p => p.debut < debut && (!p.fin || debut <= p.fin));
  if (contenante) return { fin: contenante.fin || null, err: null };
  // Sinon trou : la nouvelle est bornée par la période suivante (si devant).
  const suivante = l.find(p => p.debut > debut) || null;
  return { fin: suivante ? decalerDate(suivante.debut, -1) : null, err: null };
}

// Ajoute une période. La date de début peut tomber :
//   • dans une période existante → celle-ci se ferme la veille, la nouvelle
//     reprend la fin de l'ancienne (scission, pas de recouvrement) ;
//   • dans un trou AVANT une autre période → la nouvelle est bornée à la veille
//     de la période suivante (sa fin est affichée) ;
//   • après tout → période « en cours » (fin = null).
// Tout recouvrement est refusé.
export function insererRythme(rythmes, modeleId, debut) {
  const l = trier(rythmes).map(p => ({ ...p }));
  if (l.some(p => p.debut === debut)) return { ok: false, err: 'garde.errDejaDate' };

  const idxContenante = l.findIndex(p => p.debut < debut && (!p.fin || debut <= p.fin));
  if (idxContenante >= 0) {
    const c = l[idxContenante];
    const nouvelle = { modeleId, debut, fin: c.fin || null };   // reprend la fin de l'ancienne
    c.fin = decalerDate(debut, -1);                             // l'ancienne se ferme la veille
    return { ok: true, rythmes: trier([...l, nouvelle]) };
  }

  const suivante = l.find(p => p.debut > debut) || null;
  const nouvelle = { modeleId, debut, fin: suivante ? decalerDate(suivante.debut, -1) : null };

  // Sécurité : aucun recouvrement avec une période existante.
  const HAUT = '9999-12-31';
  const nf = nouvelle.fin || HAUT;
  const chevauche = l.some(p => debut <= (p.fin || HAUT) && p.debut <= nf);
  if (chevauche) return { ok: false, err: 'garde.errRecouvre' };

  return { ok: true, rythmes: trier([...l, nouvelle]) };
}

// Supprime une période et recolle la timeline (la précédente absorbe la plage,
// ou redevient « en cours » si on retire la dernière).
export function supprimerRythme(rythmes, index) {
  const l = trier(rythmes).map(p => ({ ...p }));
  const enleve = l[index];
  if (!enleve) return l;
  l.splice(index, 1);
  if (l.length === 0) return l;
  if (index > 0) l[index - 1].fin = (index >= l.length) ? null : enleve.fin;
  return l;
}

// Modifie une période (modèle et/ou date de départ), en respectant les bornes
// des périodes voisines. Réajuste la fin de la période précédente.
export function editerRythme(rythmes, index, { modeleId, debut, fin }) {
  const l = trier(rythmes).map(p => ({ ...p }));
  const p = l[index];
  if (!p) return { ok: false, err: 'garde.errIntrouvable' };
  const prec = l[index - 1], suiv = l[index + 1];
  const d = debut || p.debut;
  if (prec && d <= prec.debut) return { ok: false, err: 'garde.errApresPrec', errP: { date: frCourt(prec.debut) } };
  if (suiv && d >= suiv.debut) return { ok: false, err: 'garde.errAvantSuiv', errP: { date: frCourt(suiv.debut) } };
  // Date de fin : `undefined` = inchangée ; '' / null = « sans fin » ; sinon date.
  const nf = (fin === undefined) ? (p.fin || null) : (fin || null);
  if (nf) {
    if (nf < d) return { ok: false, err: 'garde.errFinApresDebut' };
    if (suiv && nf >= suiv.debut) return { ok: false, err: 'garde.errFinAvantSuiv', errP: { date: frCourt(suiv.debut) } };
  } else if (suiv) {
    return { ok: false, err: 'garde.errSansFinImpossible' };
  }
  p.modeleId = modeleId || p.modeleId;
  p.debut = d;
  p.fin = nf;
  if (prec) prec.fin = decalerDate(d, -1);
  return { ok: true, rythmes: trier(l) };
}

// ── Firestore ───────────────────────────────────────────────────
export async function lireRythmes(calId) {
  try {
    const s = await getDoc(doc(db, 'calendriers', calId));
    if (!s.exists()) return { rythmes: [], perso: [] };
    const d = s.data();
    let rythmes = Array.isArray(d.gardeRythmes) ? trier(d.gardeRythmes) : [];
    // Rétro-compat : ancien rythme unique (gardeModele) → 1 période « en cours ».
    if (rythmes.length === 0 && d.gardeModele?.id) rythmes = [{ modeleId: d.gardeModele.id, debut: d.gardeModele.dateDebut || fmt(new Date()), fin: null }];
    return { rythmes, perso: Array.isArray(d.gardeModelesPerso) ? d.gardeModelesPerso : [] };
  } catch { return { rythmes: [], perso: [] }; }
}

export async function sauverRythmes(calId, rythmes) {
  // On ne stocke QUE la définition des rythmes : la garde de chaque jour est
  // CALCULÉE à l'affichage (cf. EcranCalendrier), jamais écrite en base. Seules
  // les surcharges (échange manuel, répartition vacances) créent des docs `jours`.
  await setDoc(doc(db, 'calendriers', calId), { gardeRythmes: trier(rythmes) }, { merge: true });
}

export async function sauverModelesPerso(calId, perso) {
  await setDoc(doc(db, 'calendriers', calId), { gardeModelesPerso: perso || [] }, { merge: true });
}
